/**
 * Blood Pressure Records API Client & Standalone Offline Storage Engine
 * Communicates with Cloudflare Pages Functions (/api/records) with automatic fallback
 * to a local storage mock engine during standalone Vite previews.
 */

import {
  BloodPressureRecord,
  DailySummary,
  RecordFormData,
  DateRangeFilter,
} from '@/types/record';
import { getAuthToken } from './auth';
import { calculateDailySummary, calculateMAP, calculatePulsePressure } from '@/utils/calculations';
import { classifyBP } from '@/utils/bpClassification';
import { getTodayDateString, getCurrentTimeString, combineDateAndTime } from '@/utils/dateUtils';

const LOCAL_STORAGE_KEY = 'bp_app_records_v1';

// Initial sample records to showcase editorial journal features if empty
const DEFAULT_INITIAL_RECORDS: BloodPressureRecord[] = [
  {
    id: 1,
    systolic: 118,
    diastolic: 76,
    pulse: 70,
    measured_at: '2026-08-28T08:00:00',
    measured_date: '2026-08-28',
    measured_time: '08:00',
    arm: 'left',
    notes: '晨起空腹測量，精神放鬆',
    created_at: '2026-08-28 08:01:00',
    updated_at: '2026-08-28 08:01:00',
  },
  {
    id: 2,
    systolic: 126,
    diastolic: 82,
    pulse: 76,
    measured_at: '2026-08-28T14:30:00',
    measured_date: '2026-08-28',
    measured_time: '14:30',
    arm: 'right',
    notes: '午休醒來後測量',
    created_at: '2026-08-28 14:31:00',
    updated_at: '2026-08-28 14:31:00',
  },
  {
    id: 3,
    systolic: 122,
    diastolic: 79,
    pulse: 72,
    measured_at: '2026-08-28T21:00:00',
    measured_date: '2026-08-28',
    measured_time: '21:00',
    arm: 'left',
    notes: '睡前準備休息',
    created_at: '2026-08-28 21:01:00',
    updated_at: '2026-08-28 21:01:00',
  },
  {
    id: 4,
    systolic: 124,
    diastolic: 80,
    pulse: 74,
    measured_at: '2026-08-27T08:15:00',
    measured_date: '2026-08-27',
    measured_time: '08:15',
    arm: 'left',
    notes: '早晨常態量測',
    created_at: '2026-08-27 08:16:00',
    updated_at: '2026-08-27 08:16:00',
  },
  {
    id: 5,
    systolic: 135,
    diastolic: 86,
    pulse: 82,
    measured_at: '2026-08-27T19:40:00',
    measured_date: '2026-08-27',
    measured_time: '19:40',
    arm: 'left',
    notes: '晚餐後快走30分鐘後休息量測',
    created_at: '2026-08-27 19:41:00',
    updated_at: '2026-08-27 19:41:00',
  },
  {
    id: 6,
    systolic: 116,
    diastolic: 75,
    pulse: 68,
    measured_at: '2026-08-26T07:50:00',
    measured_date: '2026-08-26',
    measured_time: '07:50',
    arm: 'right',
    notes: '晨起常規量測',
    created_at: '2026-08-26 07:51:00',
    updated_at: '2026-08-26 07:51:00',
  },
];

/**
 * Local Storage Helper Functions
 */
function getLocalRecords(): BloodPressureRecord[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(DEFAULT_INITIAL_RECORDS));
      return [...DEFAULT_INITIAL_RECORDS];
    }
    return JSON.parse(raw);
  } catch (err) {
    console.error('Error reading from local storage:', err);
    return [...DEFAULT_INITIAL_RECORDS];
  }
}

function saveLocalRecords(records: BloodPressureRecord[]): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(records));
  } catch (err) {
    console.error('Error saving to local storage:', err);
  }
}

/**
 * Build request headers with Auth Bearer token
 */
function getAuthHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export interface QueryRecordsParams {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  order?: 'asc' | 'desc';
}

/**
 * Fetch all blood pressure records with optional date range filter
 */
export async function getRecords(
  filterOrParams?: DateRangeFilter | QueryRecordsParams
): Promise<BloodPressureRecord[]> {
  let startDate: string | undefined;
  let endDate: string | undefined;
  let limit = 100;
  let offset = 0;
  let order = 'desc';

  if (filterOrParams) {
    if ('preset' in filterOrParams) {
      startDate = filterOrParams.startDate;
      endDate = filterOrParams.endDate;
    } else {
      startDate = filterOrParams.startDate;
      endDate = filterOrParams.endDate;
      if (filterOrParams.limit !== undefined) limit = filterOrParams.limit;
      if (filterOrParams.offset !== undefined) offset = filterOrParams.offset;
      if (filterOrParams.order !== undefined) order = filterOrParams.order;
    }
  }

  // Attempt backend API call
  try {
    const query = new URLSearchParams();
    if (startDate) query.append('startDate', startDate);
    if (endDate) query.append('endDate', endDate);
    if (limit) query.append('limit', String(limit));
    if (offset) query.append('offset', String(offset));
    if (order) query.append('order', order);

    const res = await fetch(`/api/records?${query.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        return data.data;
      }
    }
  } catch (err) {
    console.warn('Backend /api/records unavailable, using local mock storage:', err);
  }

  // Local storage fallback
  let records = getLocalRecords();

  if (startDate) {
    records = records.filter((r) => r.measured_date >= startDate!);
  }
  if (endDate) {
    records = records.filter((r) => r.measured_date <= endDate!);
  }

  records.sort((a, b) => {
    const timeA = a.measured_at || `${a.measured_date}T${a.measured_time}`;
    const timeB = b.measured_at || `${b.measured_date}T${b.measured_time}`;
    return order === 'asc' ? timeA.localeCompare(timeB) : timeB.localeCompare(timeA);
  });

  return records.slice(offset, offset + limit);
}

/**
 * Fetch a single record by ID
 */
export async function getRecordById(id: number | string): Promise<BloodPressureRecord> {
  const numericId = Number(id);

  try {
    const res = await fetch(`/api/records/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success && data.data) {
        return data.data;
      }
    }
  } catch (err) {
    console.warn(`Backend /api/records/${id} unavailable, using local mock storage:`, err);
  }

  const records = getLocalRecords();
  const found = records.find((r) => r.id === numericId);
  if (!found) {
    throw new Error(`找不到編號為 ${id} 的血壓紀錄`);
  }
  return found;
}

/**
 * Create a new blood pressure record
 */
export async function createRecord(
  input: RecordFormData | Omit<BloodPressureRecord, 'id' | 'created_at' | 'updated_at'>
): Promise<BloodPressureRecord> {
  const systolic = Number(input.systolic);
  const diastolic = Number(input.diastolic);
  const pulse = Number(input.pulse);
  const measured_date = 'measuredDate' in input ? input.measuredDate : input.measured_date || getTodayDateString();
  const measured_time = 'measuredTime' in input ? input.measuredTime : input.measured_time || getCurrentTimeString();
  const measured_at = 'measured_at' in input && input.measured_at ? input.measured_at : combineDateAndTime(measured_date, measured_time);
  const notes = input.notes || '';
  const arm = input.arm || 'unspecified';

  const payload = {
    systolic,
    diastolic,
    pulse,
    measured_at,
    measured_date,
    measured_time,
    notes,
    arm,
  };

  // Attempt backend API call
  try {
    const res = await fetch('/api/records', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success && data.data) {
        return data.data;
      }
    } else {
      const errData = await res.json().catch(() => ({}));
      if (errData.error) {
        throw new Error(errData.error);
      }
    }
  } catch (err: any) {
    if (err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
      throw err;
    }
    console.warn('Backend /api/records unavailable, saving to local mock storage:', err);
  }

  // Local storage fallback
  const records = getLocalRecords();
  const maxId = records.reduce((max, r) => Math.max(max, r.id), 0);
  const nowStr = new Date().toISOString().replace('T', ' ').slice(0, 19);

  const newRecord: BloodPressureRecord = {
    id: maxId + 1,
    systolic,
    diastolic,
    pulse,
    measured_at,
    measured_date,
    measured_time,
    arm,
    notes,
    created_at: nowStr,
    updated_at: nowStr,
  };

  records.unshift(newRecord);
  saveLocalRecords(records);
  return newRecord;
}

/**
 * Update an existing blood pressure record
 */
export async function updateRecord(
  id: number | string,
  input: Partial<RecordFormData> | Partial<BloodPressureRecord>
): Promise<BloodPressureRecord> {
  const numericId = Number(id);

  // Normalize input fields
  const payload: any = { ...input };
  if ('measuredDate' in input && input.measuredDate) {
    payload.measured_date = input.measuredDate;
  }
  if ('measuredTime' in input && input.measuredTime) {
    payload.measured_time = input.measuredTime;
  }
  if (payload.measured_date && payload.measured_time) {
    payload.measured_at = combineDateAndTime(payload.measured_date, payload.measured_time);
  }

  // Attempt backend API call
  try {
    const res = await fetch(`/api/records/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success && data.data) {
        return data.data;
      }
    } else {
      const errData = await res.json().catch(() => ({}));
      if (errData.error) {
        throw new Error(errData.error);
      }
    }
  } catch (err: any) {
    if (err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
      throw err;
    }
    console.warn(`Backend /api/records/${id} unavailable, updating in local mock storage:`, err);
  }

  // Local storage fallback
  const records = getLocalRecords();
  const index = records.findIndex((r) => r.id === numericId);
  if (index === -1) {
    throw new Error(`找不到編號為 ${id} 的血壓紀錄`);
  }

  const existing = records[index];
  const updated: BloodPressureRecord = {
    ...existing,
    ...payload,
    id: numericId,
    updated_at: new Date().toISOString().replace('T', ' ').slice(0, 19),
  };

  records[index] = updated;
  saveLocalRecords(records);
  return updated;
}

/**
 * Delete a blood pressure record by ID
 */
export async function deleteRecord(id: number | string): Promise<boolean> {
  const numericId = Number(id);

  // Attempt backend API call
  try {
    const res = await fetch(`/api/records/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (res.ok) {
      const data = await res.json();
      return data.success ?? true;
    }
  } catch (err) {
    console.warn(`Backend /api/records/${id} unavailable, deleting from local mock storage:`, err);
  }

  // Local storage fallback
  const records = getLocalRecords();
  const filtered = records.filter((r) => r.id !== numericId);
  saveLocalRecords(filtered);
  return true;
}

/**
 * Get daily statistics and aggregations grouped by date
 */
export async function getDailyStats(
  params?: { startDate?: string; endDate?: string }
): Promise<DailySummary[]> {
  try {
    const query = new URLSearchParams();
    if (params?.startDate) query.append('startDate', params.startDate);
    if (params?.endDate) query.append('endDate', params.endDate);

    const res = await fetch(`/api/records/daily-stats?${query.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        return data.data;
      }
    }
  } catch (err) {
    console.warn('Backend /api/records/daily-stats unavailable, computing locally:', err);
  }

  // Local storage computation
  const records = await getRecords(params);
  const map = new Map<string, BloodPressureRecord[]>();

  for (const r of records) {
    const d = r.measured_date;
    if (!map.has(d)) {
      map.set(d, []);
    }
    map.get(d)!.push(r);
  }

  const sortedDates = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));
  return sortedDates.map((date) => calculateDailySummary(map.get(date) || [], date));
}

/**
 * Export blood pressure records as CSV formatted text or trigger browser download
 */
export async function exportRecordsCSV(
  params?: { startDate?: string; endDate?: string }
): Promise<Blob> {
  try {
    const query = new URLSearchParams();
    if (params?.startDate) query.append('startDate', params.startDate);
    if (params?.endDate) query.append('endDate', params.endDate);

    const res = await fetch(`/api/records/export?${query.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (res.ok) {
      return await res.blob();
    }
  } catch (err) {
    console.warn('Backend /api/records/export unavailable, generating CSV locally:', err);
  }

  // Client-side fallback CSV generator with UTF-8 BOM
  const records = await getRecords(params);
  const headers = [
    '記錄編號',
    '測量日期',
    '測量時間',
    '收縮壓(mmHg)',
    '舒張壓(mmHg)',
    '心跳(bpm)',
    '脈壓差(mmHg)',
    '平均動脈壓(mmHg)',
    '血壓分級狀態',
    '測量部位',
    '備註說明',
    '建立時間',
  ];

  const escapeCsv = (val: any) => {
    if (val === null || val === undefined) return '""';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return `"${str}"`;
  };

  const rows: string[] = [headers.join(',')];

  for (const r of records) {
    const category = classifyBP(r.systolic, r.diastolic);
    const pp = calculatePulsePressure(r.systolic, r.diastolic);
    const map = calculateMAP(r.systolic, r.diastolic);
    const armLabel = r.arm === 'left' ? '左手' : r.arm === 'right' ? '右手' : '未指定';

    const row = [
      r.id,
      r.measured_date,
      r.measured_time,
      r.systolic,
      r.diastolic,
      r.pulse,
      pp,
      map,
      escapeCsv(`${category.labelZh} (${category.labelEn})`),
      escapeCsv(armLabel),
      escapeCsv(r.notes || ''),
      r.created_at || '',
    ];
    rows.push(row.join(','));
  }

  // Prefix UTF-8 BOM
  const csvContent = '\uFEFF' + rows.join('\r\n');
  return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
}

/**
 * Trigger immediate browser download of exported CSV
 */
export async function downloadCSV(params?: { startDate?: string; endDate?: string }): Promise<void> {
  const blob = await exportRecordsCSV(params);
  const nowStr = getTodayDateString().replace(/-/g, '');
  const filename = `blood_pressure_records_${nowStr}.csv`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
